var __jsx = React.createElement;
import React, { useMemo } from 'react';
import { connect } from 'react-redux';
import { useTranslation } from 'next-i18next';
import { isValid, parseISO, format } from 'date-fns';
import Pill from '@spa/shared/components/Unisystem/Pill';
import { Wrapper, Title, LandlordStats } from './styles';
import MoreListings from '@spa/offer/components/MoreListings';
import { trackMoreListings } from '@spa/offer/store/actions/tracking';
import { describeLandlordStats } from '@spa/offer/utils/transformers/translations';
import { Tooltip } from '@spa/shared/index';
import Exclamation from '@spa/shared/svg/exclamation';
import { getPropertyRentBy } from '@spa/shared/utils/property';
import { RENT_TYPE } from '@spa/shared/constants/rentType';
var Landlord = function Landlord(_ref) {
  var createdAt = _ref.createdAt,
    ageRange = _ref.ageRange,
    family = _ref.family,
    gender = _ref.gender,
    pets = _ref.pets,
    occupation = _ref.occupation,
    confirmedBookings = _ref.confirmedBookings,
    responseRate = _ref.responseRate,
    responseTime = _ref.responseTime,
    isTrusted = _ref.isTrusted,
    isKeyAccount = _ref.isKeyAccount,
    recommendations = _ref.recommendations,
    isRentByProperty = _ref.isRentByProperty,
    trackMoreListingsAction = _ref.trackMoreListingsAction;
  var _useTranslation = useTranslation(),
    t = _useTranslation.t,
    language = _useTranslation.i18n.language;
  var safeCreatedAt = useMemo(function () {
    try {
      if (!createdAt) {
        return null;
      }
      var parsedDate = parseISO(createdAt);
      if (!isValid(parsedDate)) {
        return null;
      }

      // eslint-disable-next-line quotes
      return format(parsedDate, "yyyy-MM-dd'T'HH:mm:ss.SSSxxx");
    } catch (error) {
      return null;
    }
  }, [createdAt]);
  var stats = describeLandlordStats({
    t: t,
    language: language,
    ageRange: ageRange,
    family: family,
    gender: gender,
    pets: pets,
    confirmedBookings: confirmedBookings,
    responseRate: responseRate,
    responseTime: responseTime,
    createdAt: safeCreatedAt,
    isRentByProperty: isRentByProperty,
    occupation: occupation
  });
  return __jsx(Wrapper, {
    id: "Landlord"
  }, __jsx(Title, null, __jsx("h2", null, t('Landlord')), __jsx("span", null, isTrusted && __jsx(Tooltip, null, __jsx(Pill, {
    Icon: Exclamation,
    label: t('Trusted Landlord'),
    isGreen: true
  }), __jsx("p", null, t('A trusted landlord has had at least one successful booking.'))), isKeyAccount && __jsx(Tooltip, null, __jsx(Pill, {
    Icon: Exclamation,
    label: t('Professional'),
    isUniBlue: true
  }), __jsx("p", null, t('A professional landlord operates as a business, and manages a large number of properties. Many of these properties are usually student residences or apartments with lots of bedrooms. You can check the type of place at the top of this listing.'))))), __jsx(LandlordStats, null, stats.map(function (_ref2) {
    var Icon = _ref2.icon,
      text = _ref2.text,
      condition = _ref2.condition;
    return condition ? __jsx("div", {
      key: text
    }, __jsx(Icon, null), __jsx("p", null, text)) : null;
  })), recommendations !== null && recommendations !== void 0 && recommendations.data.length ? __jsx(MoreListings, {
    title: "More listings by this landlord",
    recommendations: recommendations,
    cardWidth: 224,
    cardGap: 16,
    trackListing: trackMoreListingsAction
  }) : null);
};
var mapStateToProps = function mapStateToProps(_ref3) {
  var _ref3$offer = _ref3.offer,
    _ref3$offer$offerAggr = _ref3$offer.offerAggregate,
    _ref3$offer$offerAggr2 = _ref3$offer$offerAggr.accommodation_offer;
  _ref3$offer$offerAggr2 = _ref3$offer$offerAggr2 === void 0 ? {} : _ref3$offer$offerAggr2;
  var unitType = _ref3$offer$offerAggr2.property.unit_type,
    _ref3$offer$offerAggr3 = _ref3$offer$offerAggr.property_aggregate.property,
    lifecycle = _ref3$offer$offerAggr3.lifecycle,
    typology = _ref3$offer$offerAggr3.typology,
    _ref3$offer$offerAggr4 = _ref3$offer$offerAggr3.landlord_resident;
  _ref3$offer$offerAggr4 = _ref3$offer$offerAggr4 === void 0 ? {} : _ref3$offer$offerAggr4;
  var ageRange = _ref3$offer$offerAggr4.age_range,
    family = _ref3$offer$offerAggr4.family,
    gender = _ref3$offer$offerAggr4.gender,
    pets = _ref3$offer$offerAggr4.pets,
    occupation = _ref3$offer$offerAggr4.occupation,
    _ref3$offer$offerAggr5 = _ref3$offer$offerAggr.accommodation_provider,
    _ref3$offer$offerAggr6 = _ref3$offer$offerAggr5.stats,
    confirmedBookings = _ref3$offer$offerAggr6.bookings.confirmed.total,
    responseRate = _ref3$offer$offerAggr6.response_rate,
    responseTime = _ref3$offer$offerAggr6.response_time,
    createdAt = _ref3$offer$offerAggr5.created.at,
    isTrusted = _ref3$offer$offerAggr5.verifications.trusted,
    isKeyAccount = _ref3$offer$offerAggr5.account_management.key_account,
    _ref3$offer$recommend = _ref3$offer.recommendations;
  _ref3$offer$recommend = _ref3$offer$recommend === void 0 ? {} : _ref3$offer$recommend;
  var recommendations = _ref3$offer$recommend.byAccommodationProvider;
  return {
    createdAt: createdAt,
    ageRange: ageRange,
    family: family,
    gender: gender,
    pets: pets,
    occupation: occupation,
    confirmedBookings: confirmedBookings,
    responseRate: responseRate,
    responseTime: responseTime,
    isTrusted: isTrusted,
    isKeyAccount: isKeyAccount,
    recommendations: recommendations,
    isRentByProperty: getPropertyRentBy({
      unitType: unitType,
      lifecycle: lifecycle,
      typology: typology
    }) === RENT_TYPE.ENTIRE_PROPERTY
  };
};
var mapDispatchToProps = function mapDispatchToProps(dispatch) {
  return {
    trackMoreListingsAction: function trackMoreListingsAction() {
      return dispatch(trackMoreListings());
    }
  };
};
export default connect(mapStateToProps, mapDispatchToProps)(Landlord);